import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ConnectEmbed, useActiveAccount, useDisconnect } from "thirdweb/react";
import { createAuth, signLoginPayload } from "thirdweb/auth";
import { getUserEmail } from "thirdweb/wallets";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import Loading from "../../../../components/LoadingSkeleton/Loading";
import Typography from "../../../../components/Typography/Typography";
import { ACCESS_TOKEN_NAME, REFRESH_TOKEN_NAME } from "../../../../constants";
import { useLoginMutation } from "../../../../services/authService";
import { authActions } from "../../../../services/authSlice";
import { logEvent } from "../../../../utils/amplitudeUtlis";
import { client, wallets } from "../../../../constants/client";
import { NETWORKS } from "../../../../web3/networks";
import "./auth.scss";

export default function CustomerAuthPage() {
    const navigate = useNavigate();
    const [signIn] = useLoginMutation();
    const location = useLocation();
    const { projectId } = useParams();
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const { disconnect } = useDisconnect();
    const queryParams = new URLSearchParams(location.search);
    const invitation = queryParams.get("invitation");

    // Using new thirdweb hook
    const activeAccount = useActiveAccount();

    const chainId = process.env.REACT_APP_DEFAULT_CHAIN_ID || "11155111";
    const activeChainData = NETWORKS[chainId];

    // Unified authentication function
    const authenticateUser = useCallback(
        async (payload, signature, email) => {
            setIsLoading(true);

            try {
                // Log event for analytics
                logEvent({
                    eventName: "taas_user_login_initiated",
                    eventProperties: {
                        project_id: projectId,
                    },
                });

                // Call API
                const signInRes = await signIn({
                    payload: payload,
                    signature: signature,
                    email: email,
                    invitationToken: invitation || "",
                    external: true,
                    project_id: projectId,
                });

                if (!signInRes.data) {
                    const errorMessage =
                        signInRes?.error?.data?.errors?.[0] || "Login failed";
                    toast.error(errorMessage);

                    logEvent({
                        eventName: "taas_user_login_failed",
                        eventProperties: {
                            project_id: projectId,
                            status: "Failed",
                        },
                    });

                    return { success: false, error: errorMessage };
                }

                const { tokens, user } = signInRes.data.data;

                // Store tokens
                if (tokens?.access)
                    localStorage.setItem(ACCESS_TOKEN_NAME, tokens.access);
                if (tokens?.refresh)
                    localStorage.setItem(REFRESH_TOKEN_NAME, tokens.refresh);

                dispatch(authActions.login(user));

                logEvent({
                    eventName: "taas_user_login_completed",
                    eventProperties: {
                        project_id: projectId,
                        email: email,
                        role: user?.role,
                        status: "Success",
                    },
                });

                // Navigate based on user role
                navigate(
                    user.role === "CUSTOMER"
                        ? `/token-app/${projectId}`
                        : "/overview",
                );

                return { success: true, user };
            } catch (error) {
                // Don't handle AbortError as an actual error
                if (error.name === "AbortError")
                    return { success: false, aborted: true };

                console.error("Error during authentication:", error);
                toast.error(
                    error.message || "Authentication failed. Please try again.",
                );

                logEvent({
                    eventName: "taas_user_login_failed",
                    eventProperties: {
                        project_id: projectId,
                        status: "Error",
                        error: error.message,
                    },
                });

                return {
                    success: false,
                    error: error.message || "Authentication failed",
                };
            } finally {
                setIsLoading(false);
            }
        },
        [signIn, dispatch, navigate, projectId, invitation],
    );

    // Direct login function using the unified authenticateUser
    const login = useCallback(async () => {
        try {
            setIsLoading(true);

            const auth = createAuth({
                domain: "hyperglade.local",
                client: client,
                login: {
                    resources: ["https://hyperglade.io"],
                    payloadExpirationTimeSeconds: 86400,
                },
            });

            const loginPayload = await auth.generatePayload({
                address: activeAccount?.address,
                chainId: activeChainData?.chainId,
            });

            const signedPayload = await signLoginPayload({
                payload: loginPayload,
                account: activeAccount,
            });

            const email = await getUserEmail({ client });

            return authenticateUser(
                signedPayload?.payload,
                signedPayload?.signature,
                email,
            );
        } catch (error) {
            console.error("Error preparing authentication:", error);
            toast.error("Failed to prepare authentication. Please try again.");
            setIsLoading(false);
            return { success: false, error: error.message };
        }
    }, [activeAccount, authenticateUser, activeChainData]);

    // Auto-login when account is available
    useEffect(() => {
        if (activeAccount?.address) {
            login();
        }
    }, [activeAccount?.address, login]);

    return (
        <div
            className="d-flex align-items-center justify-content-center"
            style={{ height: "100vh" }}
        >
            <div className="container-fluid p-4 col-12 col-md-10">
                <div className="row">
                    <div className="col-12">
                        <Typography variant="h2" className="pt-2 text-center">
                            Access your account
                        </Typography>

                        <Typography
                            variant="body1"
                            className="pt-2 text-center"
                        >
                            Please enter your credentials to access the customer
                            portal.
                        </Typography>
                    </div>
                    <div className="col-12 d-flex justify-content-center pt-2">
                        <div className="d-flex flex-column justify-content-center">
                            <ConnectEmbed
                                client={client}
                                wallets={wallets}
                                size="compact"
                                header={{
                                    title: "Sign in",
                                }}
                                style={{
                                    minWidth: "360px",
                                    boxShadow:
                                        "0px 4px 4px rgba(0, 0, 0, 0.25)",
                                }}
                                auth={{
                                    getLoginPayload: async (params) => {
                                        setIsLoading(true);
                                        try {
                                            const auth = createAuth({
                                                domain: "hyperglade.local",
                                                client: client,
                                                login: {
                                                    resources: [
                                                        "https://hyperglade.io",
                                                    ],
                                                    payloadExpirationTimeSeconds: 86400,
                                                },
                                            });

                                            return auth.generatePayload({
                                                address: params.address,
                                                chainId:
                                                    params.chainId ||
                                                    activeChainData?.chainId,
                                            });
                                        } catch (error) {
                                            setIsLoading(false);
                                            throw error;
                                        }
                                    },
                                    doLogin: async (params) => {
                                        try {
                                            const email = await getUserEmail({
                                                client,
                                            });
                                            return authenticateUser(
                                                params.payload,
                                                params.signature,
                                                email,
                                            );
                                        } catch (error) {
                                            console.error(
                                                "Login error:",
                                                error,
                                            );
                                            return {
                                                success: false,
                                                error:
                                                    error.message ||
                                                    "Authentication failed",
                                            };
                                        }
                                    },
                                    isLoggedIn: async () => {
                                        const accessToken =
                                            localStorage.getItem(
                                                ACCESS_TOKEN_NAME,
                                            );
                                        return { isLoggedIn: !!accessToken };
                                    },
                                    doLogout: async () => {
                                        await disconnect();
                                        localStorage.removeItem(
                                            ACCESS_TOKEN_NAME,
                                        );
                                        localStorage.removeItem(
                                            REFRESH_TOKEN_NAME,
                                        );
                                        dispatch(authActions.logout());
                                        return { success: true };
                                    },
                                }}
                            />
                        </div>
                    </div>
                    {isLoading && (
                        <div className="d-flex justify-content-center align-items-center mt-3">
                            <Loading />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
